import { productsAPI } from '@/api/products';

function getProductUnit(categoryId) {
  const units = {
    1: '200g',
    2: '150g',
    3: '300g',
    4: '250g',
    5: '0.5L',
    6: '100g'
  };
  return units[categoryId] || '1 pc';
}

function getProductCalories(product) {
  const baseCalories = {
    1: 450,
    2: 320,
    3: 580,
    4: 420,
    5: 150,
    6: 210
  };
  return baseCalories[product.category] || 350;
}

function getProductRating(productId) {
  return (4 + (productId % 10) / 10).toFixed(1);
}

export default {
  namespaced: true,
  state: {
    products: [],
    featuredProducts: [],
    currentProduct: null,
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      pageSize: 12
    },
    filters: {
      category: null,
      search: '',
      sortBy: '-created_at',
      minPrice: null,
      maxPrice: null
    }
  },
  
  getters: {
    products: (state) => state.products || [],
    featuredProducts: (state) => state.featuredProducts || [],
    currentProduct: (state) => state.currentProduct,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    pagination: (state) => state.pagination,
    filters: (state) => state.filters,
    
    getProductsByCategory: (state) => (categoryId) => {
      return (state.products || []).filter(p => p.category === parseInt(categoryId));
    },
    
    getProductById: (state) => (id) => {
      return (state.products || []).find(p => p.id === parseInt(id));
    },
    
    getProductBySlug: (state) => (slug) => {
      return (state.products || []).find(p => p.slug === slug);
    }
  },
  
  mutations: {
    SET_PRODUCTS(state, { results, count, page, pageSize }) {
      state.products = results || [];
      state.pagination = {
        currentPage: page || 1,
        totalPages: Math.ceil((count || 0) / (pageSize || 12)),
        totalItems: count || 0,
        pageSize: pageSize || 12
      };
    },
    
    SET_FEATURED_PRODUCTS(state, products) {
      state.featuredProducts = products || [];
    },
    
    SET_CURRENT_PRODUCT(state, product) {
      state.currentProduct = product;
    },
    
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
    
    RESET_FILTERS(state) {
      state.filters = {
        category: null,
        search: '',
        sortBy: '-created_at',
        minPrice: null,
        maxPrice: null
      };
    },
    
    ADD_PRODUCT(state, product) {
      if (!state.products) state.products = [];
      state.products.unshift(product);
    },
    
    UPDATE_PRODUCT(state, updatedProduct) {
      if (!state.products) return;
      const index = state.products.findIndex(p => p.id === updatedProduct.id);
      if (index !== -1) {
        state.products[index] = updatedProduct;
      }
      if (state.currentProduct?.id === updatedProduct.id) {
        state.currentProduct = updatedProduct;
      }
    },
    
    DELETE_PRODUCT(state, productId) {
      if (!state.products) return;
      state.products = state.products.filter(p => p.id !== productId);
      if (state.currentProduct?.id === productId) {
        state.currentProduct = null;
      }
    },
    
    CLEAR_ERROR(state) {
      state.error = null;
    }
  },
  
  actions: {
    async fetchProducts({ commit, state }, params = {}) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const queryParams = {
          page: params.page || state.pagination.currentPage,
          page_size: params.pageSize || state.pagination.pageSize,
          ...state.filters,
          ...params
        };
        
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] === null || queryParams[key] === '') {
            delete queryParams[key];
          }
        });
        
        const response = await productsAPI.getProducts(queryParams);
        
        // Handle both array and paginated response
        let products = [];
        let count = 0;
        
        if (Array.isArray(response.data)) {
          products = response.data;
          count = products.length;
        } else if (response.data.results) {
          products = response.data.results;
          count = response.data.count || 0;
        } else {
          products = response.data || [];
          count = products.length;
        }
        
        const productsWithUI = products.map(product => ({
          ...product,
          unit: getProductUnit(product.category),
          calories: getProductCalories(product),
          rating: getProductRating(product.id)
        }));
        
        commit('SET_PRODUCTS', {
          results: productsWithUI,
          count: count,
          page: queryParams.page,
          pageSize: queryParams.page_size
        });
        
        return productsWithUI;
      } catch (error) {
        console.error('Fetch products error:', error);
        const errorMessage = error.response?.data?.error || 'Failed to fetch products';
        commit('SET_ERROR', errorMessage);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchFeaturedProducts({ commit }) {
      try {
        const response = await productsAPI.getFeaturedProducts();
        let products = [];
        
        if (Array.isArray(response.data)) {
          products = response.data;
        } else if (response.data.results) {
          products = response.data.results;
        } else {
          products = response.data || [];
        }
        
        const productsWithUI = products.map(product => ({
          ...product,
          unit: getProductUnit(product.category),
          calories: getProductCalories(product),
          rating: getProductRating(product.id)
        }));
        
        commit('SET_FEATURED_PRODUCTS', productsWithUI);
        return productsWithUI;
      } catch (error) {
        console.error('Failed to fetch featured products:', error);
        return [];
      }
    },
    
    async fetchProduct({ commit }, slug) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await productsAPI.getProduct(slug);
        const product = response.data;
        
        const productWithUI = {
          ...product,
          unit: getProductUnit(product.category),
          calories: getProductCalories(product),
          rating: getProductRating(product.id)
        };
        
        commit('SET_CURRENT_PRODUCT', productWithUI);
        return productWithUI;
      } catch (error) {
        console.error('Fetch product error:', error);
        commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch product');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    updateFilters({ commit, dispatch }, filters) {
      commit('SET_FILTERS', filters);
      dispatch('fetchProducts', { page: 1 });
    },
    
    resetFilters({ commit, dispatch }) {
      commit('RESET_FILTERS');
      dispatch('fetchProducts', { page: 1 });
    },
    
    async createProduct({ commit }, productData) {
      const response = await productsAPI.createProduct(productData);
      const newProduct = {
        ...response.data,
        unit: getProductUnit(response.data.category),
        calories: getProductCalories(response.data),
        rating: getProductRating(response.data.id)
      };
      commit('ADD_PRODUCT', newProduct);
      return newProduct;
    },
    
    async updateProduct({ commit }, { id, data }) {
      const response = await productsAPI.updateProduct(id, data);
      const updatedProduct = {
        ...response.data,
        unit: getProductUnit(response.data.category),
        calories: getProductCalories(response.data),
        rating: getProductRating(response.data.id)
      };
      commit('UPDATE_PRODUCT', updatedProduct);
      return updatedProduct;
    },
    
    async deleteProduct({ commit }, id) {
      await productsAPI.deleteProduct(id);
      commit('DELETE_PRODUCT', id);
    }
  }
};